// ✅ MAJ — src/pages/Admin/Casting/components/CastingScreenPreview.jsx
import React, { useMemo, useState } from "react";
import {
  FaTimes,
  FaBullhorn,
  FaGripHorizontal,
  FaExclamationTriangle,
  FaTrophy,
  FaSyncAlt,
  FaExchangeAlt,
} from "react-icons/fa";

const stripHtml = (s = "") =>
  String(s)
    .replace(/<[^>]*>/g, "")
    .trim();

// ✅ Nouveau: découpe "M-32kg" => { gender:"M", rest:"-32kg" }
const parseCategoryLabel = (label = "") => {
  const raw = String(label).trim();
  if (!raw) return { gender: null, rest: "" };

  const first = raw[0]?.toUpperCase();
  if (first !== "M" && first !== "F") {
    return { gender: null, rest: raw };
  }

  let rest = raw.slice(1).trim();

  // normalise " - 32 kg" => "-32 kg"
  rest = rest
    .replace(/^\s*-\s*/, "-")
    .replace(/\s+/g, " ")
    .trim();

  return { gender: first, rest: rest || raw.slice(1).trim() };
};

const genderBadgeClass = (g) => {
  if (g === "M") return "bg-sky-600 text-white ring-sky-200/40";
  if (g === "F") return "bg-pink-600 text-white ring-pink-200/40";
  return "bg-slate-800/60 text-white ring-white/10";
};

// --- MESSAGE THEME ---
const inferMessageType = (m) => {
  if (!m) return "INFO";

  const raw =
    String(m.type || m.kind || m.category || "")
      .trim()
      .toUpperCase() ||
    `${m.title || ""} ${stripHtml(m.content || "")}`.toUpperCase();

  if (
    raw.includes("ALERTE") ||
    raw.includes("ALERT") ||
    raw.includes("CALL") ||
    raw.includes("APPEL") ||
    raw.includes("URGENT") ||
    raw.includes("ATTENTION")
  ) {
    return "ALERT";
  }

  if (
    raw.includes("MEDAIL") ||
    raw.includes("MÉDAIL") ||
    raw.includes("PODIUM") ||
    raw.includes("REMISE") ||
    raw.includes("TROPH") ||
    raw.includes("TROPHY")
  ) {
    return "PODIUM";
  }

  return "INFO";
};

const messageTheme = (type) => {
  if (type === "ALERT") {
    return {
      wrapper:
        "bg-gradient-to-br from-rose-700 via-red-700 to-red-900 text-white",
      pill: "bg-white/15 ring-1 ring-white/25 text-white",
      Icon: FaBullhorn,
      iconWrap: "bg-white/15 ring-2 ring-white/20",
      title: "Appel",
    };
  }
  if (type === "PODIUM") {
    return {
      wrapper:
        "bg-gradient-to-br from-sky-700 via-blue-700 to-indigo-900 text-white",
      pill: "bg-white/15 ring-1 ring-white/25 text-white",
      Icon: FaTrophy,
      iconWrap: "bg-white/15 ring-2 ring-white/20",
      title: "Podium",
    };
  }
  return {
    wrapper:
      "bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white",
    pill: "bg-white/15 ring-1 ring-white/20 text-white",
    Icon: FaExclamationTriangle,
    iconWrap: "bg-white/10 ring-2 ring-white/15",
    title: "Info",
  };
};

// ✅ Détection surface TC (si tu stockes TC comme un "mat")
const isTCMat = (m) => {
  const lbl = String(m?.label || "")
    .trim()
    .toUpperCase();
  const num = String(m?.mat_number ?? "")
    .trim()
    .toUpperCase();
  return (
    lbl === "TC" ||
    num === "TC" ||
    lbl.includes("TABLE CENTRALE") ||
    lbl.includes("CENTRALE")
  );
};

const isPodiumMat = (m) => {
  const lbl = String(m?.label || "")
    .trim()
    .toUpperCase();
  const num = String(m?.mat_number ?? "")
    .trim()
    .toUpperCase();
  return lbl === "PODIUM" || num === "PODIUM" || lbl.includes("PODIUM");
};

const isMatOpen = (m) => m?.is_open !== false && m?.is_open !== 0;

export default function CastingScreenPreview({
  event,
  castState,
  mats = [],
  messages = [],
  onClose,
  onRefresh,
  onSetMode,
}) {
  const mode = castState?.mode || "MATS";

  const [refreshing, setRefreshing] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);

  const handleRefresh = async () => {
    if (typeof onRefresh !== "function") return;
    try {
      setRefreshing(true);
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggleMode = async () => {
    if (typeof onSetMode !== "function") return;
    const nextMode = mode === "MATS" ? "MESSAGE" : "MATS";
    try {
      setSwitchingMode(true);
      await onSetMode(nextMode);
    } finally {
      setSwitchingMode(false);
    }
  };

  const activeMessage = useMemo(() => {
    const activeId = castState?.active_message_id;
    const list = (messages || []).filter((m) => m?.is_active !== false);

    return (
      list.find((m) => m.id === activeId) ||
      list.find((m) => m.is_pinned) ||
      list[0] ||
      null
    );
  }, [messages, castState?.active_message_id]);

  const messageType = useMemo(
    () => inferMessageType(activeMessage),
    [activeMessage]
  );
  const theme = useMemo(() => messageTheme(messageType), [messageType]);

  // ✅ 10 surfaces max, triées, ouvertes
  const visibleMats = useMemo(() => {
    const byOrder = [...(mats || [])].sort(
      (a, b) => (a.sort_order ?? a.mat_number) - (b.sort_order ?? b.mat_number)
    );

    return byOrder.slice(0, 10);
  }, [mats]);

  return (
    <div className="fixed inset-0 z-[99999] text-white">
      {/* Top bar (toujours visible) */}
      <div className="h-16 px-4 flex items-center justify-between bg-slate-950 border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-bold truncate text-2xl">
            {event?.titre || "Écran compétition"}
          </div>
          <div className="text-xs opacity-80">
            Mode : {mode === "MATS" ? "TAPIS" : "MESSAGE"}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleMode}
            disabled={switchingMode || typeof onSetMode !== "function"}
            className={[
              "inline-flex items-center gap-2 h-9 px-3 rounded-xl",
              "bg-white/10 hover:bg-white/20",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            ].join(" ")}
            title="Permuter l’affichage (tapis / message)"
          >
            <FaExchangeAlt className={switchingMode ? "animate-spin" : ""} />
            {mode === "MATS" ? "Afficher message" : "Afficher tapis"}
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing || typeof onRefresh !== "function"}
            className={[
              "inline-flex items-center gap-2 h-9 px-3 rounded-xl",
              "bg-white/10 hover:bg-white/20",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            ].join(" ")}
            title={
              typeof onRefresh !== "function"
                ? "Aucun callback onRefresh fourni"
                : "Recharger les données"
            }
          >
            <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Mise à jour..." : "Mise à jour"}
          </button>

          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FaTimes /> Retour admin
          </button>
        </div>
      </div>

      {/* Body */}
      {mode === "MESSAGE" ? (
        <div className="h-[calc(100%-4rem)] bg-slate-950 flex items-center justify-center p-6 sm:p-10">
          <div
            className={[
              "w-full max-w-6xl rounded-[2rem] shadow-2xl",
              "ring-2 ring-white/10",
              "p-8 sm:p-12",
              theme.wrapper,
            ].join(" ")}
          >
            <div className="flex items-start gap-6">
              <div
                className={[
                  "shrink-0 h-20 w-20 sm:h-28 sm:w-28 rounded-3xl",
                  "flex items-center justify-center",
                  theme.iconWrap,
                ].join(" ")}
              >
                <theme.Icon className="text-4xl sm:text-6xl" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={[
                      "inline-flex items-center px-4 py-2 rounded-full",
                      "text-sm sm:text-lg font-extrabold uppercase tracking-widest",
                      theme.pill,
                    ].join(" ")}
                  >
                    {theme.title}
                  </span>

                  <span className="text-sm sm:text-lg opacity-90">
                    {activeMessage?.target_mat_number
                      ? `Tapis ${activeMessage.target_mat_number}`
                      : "Global"}
                  </span>
                </div>

                <h2 className="mt-5 text-4xl sm:text-6xl font-extrabold leading-tight">
                  {activeMessage?.title || "Information"}
                </h2>

                <p className="mt-6 text-2xl sm:text-4xl font-semibold leading-snug whitespace-pre-wrap">
                  {stripHtml(activeMessage?.content || "Aucun message actif.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // ✅ MODE TAPIS — 2 lignes / 5 colonnes (10 max) sans scroll
        <div className="h-[calc(100%-4rem)] bg-emerald-800 p-4">
          <div className="h-full flex flex-col min-h-0">
            <div className="flex items-center gap-3 text-sm opacity-90 mb-3">
              <FaGripHorizontal />
              <span className="uppercase tracking-wider">
                Répartition des surfaces
              </span>
            </div>

            {visibleMats.length === 0 ? (
              <div className="text-white/90 text-lg font-semibold">
                Aucun tapis actif.
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-hidden">
                <div
                  className={[
                    "h-full grid gap-6",
                    // responsive: sur grand écran => 5 colonnes, 2 lignes fixes
                    "grid-cols-2",
                    "sm:grid-cols-3",
                    "lg:grid-cols-5 lg:grid-rows-2",
                  ].join(" ")}
                >
                  {visibleMats.map((m) => {
                    const open = isMatOpen(m);
                    if (!open) {
                      return (
                        <div
                          key={m.id || m.mat_number}
                          className={[
                            "h-full min-h-0 rounded-[2rem] ring-2 shadow-2xl",
                            "bg-gradient-to-br from-slate-700/50 via-slate-800/40 to-slate-900/50"
                          ].join(" ")}
                        />
                      );
                    }

                    // ✅ tu peux aussi gérer TC via un slot "TC" si besoin (plus bas)
                    if (isTCMat(m)) {
                      return (
                        <div
                          key={m.id || m.mat_number || "TC"}
                          className="h-full flex items-center justify-center"
                        >
                          <div
                            className={[
                              "w-full aspect-square",
                              "rounded-[2rem] shadow-2xl ring-2",
                              "bg-slate-100 text-slate-900 ring-white/40",
                              "flex items-center justify-center",
                            ].join(" ")}
                          >
                            <div className="text-7xl lg:text-8xl font-black tracking-widest">
                              TC
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (isPodiumMat(m)) {
                      return (
                        <div
                          key={m.id || m.mat_number || "PODIUM"}
                          className="h-full flex items-center justify-center"
                        >
                          <div
                            className={[
                              "w-full aspect-square",
                              "rounded-[2rem] shadow-2xl ring-2",
                              "bg-indigo-100 text-slate-900 ring-white/40",
                              "flex items-center justify-center",
                            ].join(" ")}
                          >
                            <div className="flex flex-col items-center justify-center gap-4">
                              <FaTrophy className="text-6xl lg:text-7xl" />
                              <div className="text-4xl lg:text-5xl font-black tracking-widest">
                                PODIUM
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    const slots = (m?.Slots || [])
                      .filter(
                        (s) =>
                          s?.is_active !== false &&
                          String(s?.label || "").trim()
                      )
                      .sort(
                        (a, b) => Number(a.slot_index) - Number(b.slot_index)
                      );

                    return (
                      <div
                        key={m.id || m.mat_number}
                        className={[
                          "h-full min-h-0 flex flex-col",
                          "p-4 rounded-[2rem] ring-2 shadow-2xl",
                          "bg-yellow-300 text-slate-900 ring-yellow-100/70",
                        ].join(" ")}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="text-4xl lg:text-5xl font-extrabold leading-none">
                            {m?.label || `TAPIS ${m?.mat_number}`}
                          </div>
                        </div>

                        <div className="mt-4 space-y-3 flex-1 min-h-0">
                          {slots.length === 0 ? (
                            <div className="text-lg opacity-80 font-semibold">
                              Aucune catégorie
                            </div>
                          ) : (
                            // ✅ garde 3 catégories max (sinon ça surcharge)
                            slots.slice(0, 3).map((s) => {
                              const raw = String(s.label || "").trim();
                              const upper = raw.toUpperCase();

                              // ✅ Slot spécial "TC" (si jamais tu le stockes comme catégorie)
                              if (
                                upper === "TC" ||
                                upper.includes("TABLE CENTRALE")
                              ) {
                                return (
                                  <div
                                    key={`${m.mat_number}-${s.slot_index}`}
                                    className={[
                                      "rounded-2xl ring-1 ring-black/10",
                                      "bg-slate-100 text-slate-900",
                                      "p-4 flex items-center justify-center",
                                      "min-h-[6.5rem]",
                                    ].join(" ")}
                                  >
                                    <div className="text-5xl lg:text-6xl font-black tracking-widest">
                                      TC
                                    </div>
                                  </div>
                                );
                              }

                              const { gender, rest } = parseCategoryLabel(raw);

                              return (
                                <div
                                  key={`${m.mat_number}-${s.slot_index}`}
                                  className="rounded-2xl bg-white/80 ring-1 ring-black/10 p-3"
                                >
                                  <div className="flex items-start gap-3">
                                    {gender ? (
                                      <span
                                        className={[
                                          "shrink-0 inline-flex items-center justify-center",
                                          "h-12 w-12 rounded-full text-3xl font-extrabold",
                                          "ring-1",
                                          genderBadgeClass(gender),
                                        ].join(" ")}
                                        title={
                                          gender === "M"
                                            ? "Masculin"
                                            : "Féminin"
                                        }
                                      >
                                        {gender}
                                      </span>
                                    ) : null}

                                    <div className="min-w-0">
                                      <div className="text-3xl sm:text-5xl lg:text-5xl font-extrabold leading-tight break-words">
                                        {gender ? rest : raw}
                                      </div>

                                      {s.note ? (
                                        <div className="text-lg opacity-90 mt-1 font-semibold">
                                          {s.note}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
